import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import type { RankingGameMode, ScoringRules } from '../../types';

/**
 * Shared scoring-rules editor, used by the ranking create modal and the
 * "Editar pontuação" modal. Keeps the two forms — and the wording of the
 * per-game vs games-difference modes — identical.
 */

/** Starting values for a brand new ranking, per game mode. */
export const DEFAULT_SCORING_RULES: ScoringRules = {
  matchWin: 70,
  matchLoss: 10,
  woWin: 70,
  woLoss: -50,
  gameMode: 'per_game',
  perGame: 5,
  gameDiffWin: 5,
  gameDiffLoss: 5,
  allowWildcardCatchUp: true,
};

/**
 * A losing base of 10 makes sense when games won are added back on top of it,
 * but not when the margin is subtracted from it. Suggest a base per mode so a
 * fresh ranking that flips modes doesn't end up handing out negative points.
 */
const DEFAULT_MATCH_LOSS_BY_MODE: Record<RankingGameMode, number> = {
  per_game: DEFAULT_SCORING_RULES.matchLoss,
  game_diff: 55,
};

/** Points a team earns for `teamGames`x`opponentGames` — mirrors rankingScoring.ts. */
export function previewMatchPoints(
  rules: ScoringRules,
  teamGames: number,
  opponentGames: number
): number {
  const won = teamGames > opponentGames;
  if (rules.gameMode === 'game_diff') {
    const diff = Math.abs(teamGames - opponentGames);
    return won
      ? rules.matchWin + diff * rules.gameDiffWin
      : rules.matchLoss - diff * rules.gameDiffLoss;
  }
  return (won ? rules.matchWin : rules.matchLoss) + teamGames * rules.perGame;
}

export function ScoringRulesFields({
  value,
  onChange,
  /**
   * When switching modes, move the losing base to that mode's default — but
   * only if it is still untouched. Enabled while creating a ranking; off while
   * editing one, where a silent base change would rewrite everyone's points.
   */
  adjustBaseOnModeChange = false,
}: {
  value: ScoringRules;
  onChange: (patch: Partial<ScoringRules>) => void;
  adjustBaseOnModeChange?: boolean;
}) {
  const handleModeChange = (gameMode: RankingGameMode) => {
    if (gameMode === value.gameMode) return;

    const patch: Partial<ScoringRules> = { gameMode };
    if (
      adjustBaseOnModeChange &&
      value.matchLoss === DEFAULT_MATCH_LOSS_BY_MODE[value.gameMode]
    ) {
      patch.matchLoss = DEFAULT_MATCH_LOSS_BY_MODE[gameMode];
    }
    onChange(patch);
  };

  const winPreview = previewMatchPoints(value, 6, 3);
  const lossPreview = previewMatchPoints(value, 3, 6);

  return (
    <>
      <ScoringNumberInput
        label="Vitória"
        value={value.matchWin}
        onChange={(matchWin) => onChange({ matchWin })}
      />
      <ScoringNumberInput
        label="Derrota"
        value={value.matchLoss}
        onChange={(matchLoss) => onChange({ matchLoss })}
      />
      <ScoringNumberInput
        label="WO (presente)"
        value={value.woWin}
        onChange={(woWin) => onChange({ woWin })}
      />
      <ScoringNumberInput
        label="WO (ausente)"
        value={value.woLoss}
        onChange={(woLoss) => onChange({ woLoss })}
      />

      {/* Game bonus mode */}
      <div className="pt-3 border-t border-white/10 space-y-3">
        <p className="text-xs text-white/40 uppercase tracking-wider">Pontos por games</p>

        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/5">
          <ModeTab
            label="Por game"
            isActive={value.gameMode === 'per_game'}
            onClick={() => handleModeChange('per_game')}
          />
          <ModeTab
            label="Saldo de games"
            isActive={value.gameMode === 'game_diff'}
            onClick={() => handleModeChange('game_diff')}
          />
        </div>

        {value.gameMode === 'per_game' ? (
          <ScoringNumberInput
            label="Por game vencido"
            value={value.perGame}
            onChange={(perGame) => onChange({ perGame })}
          />
        ) : (
          <>
            <ScoringNumberInput
              label="Por game de saldo (vitória)"
              value={value.gameDiffWin}
              onChange={(gameDiffWin) => onChange({ gameDiffWin })}
            />
            <ScoringNumberInput
              label="Por game de saldo (derrota)"
              value={value.gameDiffLoss}
              onChange={(gameDiffLoss) => onChange({ gameDiffLoss })}
            />
          </>
        )}

        <div className="flex gap-2 text-[11px] text-white/40 leading-snug">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          <p>
            {value.gameMode === 'per_game'
              ? 'Cada game vencido soma pontos, mesmo na derrota.'
              : 'A diferença de games soma para quem vence e desconta de quem perde.'}{' '}
            Em um 6/3: <span className="text-white/70">{winPreview}</span> pts para os
            vencedores, <span className="text-white/70">{lossPreview}</span> para os perdedores.
          </p>
        </div>
      </div>

      {/* Wildcard catch-up */}
      <div className="pt-3 border-t border-white/10">
        <button
          type="button"
          onClick={() => onChange({ allowWildcardCatchUp: !value.allowWildcardCatchUp })}
          className="w-full flex items-center justify-between gap-3 text-left"
        >
          <span className="text-sm text-white/60">Coringa recupera pontos</span>
          <span
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
              value.allowWildcardCatchUp ? 'bg-turquoise' : 'bg-white/15'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                value.allowWildcardCatchUp ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </span>
        </button>
        <p className="text-[11px] text-white/40 mt-2 leading-snug">
          {value.allowWildcardCatchUp
            ? 'Jogadores atrasados em rodadas podem recuperar pontos jogando como coringa.'
            : 'Coringas apenas completam os grupos para que as partidas aconteçam, sem pontuar.'}
        </p>
      </div>
    </>
  );
}

function ModeTab({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`py-2 rounded-lg text-xs font-semibold transition-colors ${
        isActive ? 'bg-turquoise text-deep-blue' : 'text-white/50 hover:text-white/80'
      }`}
    >
      {label}
    </button>
  );
}

/** Number field that tolerates transient "" and "-" while typing. */
export function ScoringNumberInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const [raw, setRaw] = useState(value.toString());

  useEffect(() => {
    setRaw(value.toString());
  }, [value]);

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-white/60">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={raw}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '' || v === '-' || /^-?\d+$/.test(v)) {
            setRaw(v);
            const parsed = parseInt(v);
            if (!isNaN(parsed)) onChange(parsed);
          }
        }}
        onBlur={() => {
          const parsed = parseInt(raw);
          if (isNaN(parsed)) {
            setRaw('0');
            onChange(0);
          }
        }}
        className="w-20 shrink-0 px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-center text-sm focus:outline-none focus:border-turquoise/50"
      />
    </div>
  );
}

/** Read-only rendering of the scoring rules (Info / Settings tabs). */
export function ScoringRulesSummary({ rules }: { rules: ScoringRules }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <ScoringDisplay label="Vitória" value={rules.matchWin} />
        <ScoringDisplay label="Derrota" value={rules.matchLoss} />
        <ScoringDisplay label="WO (presente)" value={rules.woWin} />
        <ScoringDisplay label="WO (ausente)" value={rules.woLoss} />
        {rules.gameMode === 'per_game' ? (
          <ScoringDisplay label="Por game vencido" value={rules.perGame} />
        ) : (
          <>
            <ScoringDisplay label="Saldo (vitória)" value={rules.gameDiffWin} />
            <ScoringDisplay label="Saldo (derrota)" value={-rules.gameDiffLoss} />
          </>
        )}
        <ScoringFlagDisplay label="Coringa recupera pontos" enabled={rules.allowWildcardCatchUp} />
      </div>
      <p className="text-[11px] text-white/40 leading-snug">
        Em um 6/3: {previewMatchPoints(rules, 6, 3)} pts para os vencedores,{' '}
        {previewMatchPoints(rules, 3, 6)} para os perdedores.
      </p>
    </div>
  );
}

function ScoringDisplay({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-lg bg-white/5">
      <span className="text-white/50">{label}</span>
      <span className={`font-bold tabular-nums ${value < 0 ? 'text-coral' : value > 0 ? 'text-turquoise' : 'text-white/40'}`}>
        {value > 0 ? `+${value}` : value}
      </span>
    </div>
  );
}

function ScoringFlagDisplay({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="col-span-2 flex items-center justify-between p-2 rounded-lg bg-white/5">
      <span className="text-white/50">{label}</span>
      <span className={`font-bold ${enabled ? 'text-turquoise' : 'text-white/40'}`}>
        {enabled ? 'Sim' : 'Não'}
      </span>
    </div>
  );
}
